import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../globals/dictionary'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useTwinTranslation from '../useTwinTranslation'
import { FormElement, FormRender } from '../../../forms/FormRenderer/types'
import { CustomerPayload } from '../../reducers/customers/customer'
import { CustomSelectAndCreateSectionCustomer } from '../../../specificComponents/Customers/CustomSelectSectionCustomers'
import { twinFetchPostJSON } from '../../globals/data'
import { useNavigate } from 'react-router'
import { OnSubmit } from '../../../forms/TwinForm/types'
import locations from '../../globals/locations'
import { getDictionaryStates } from '../../globals/countries'
import { sortCountries } from '../../globals/sortCountries'
import { getConfigParam, getDictionaryCountries } from '../../reducers/getters'
import useCustomerTypesDict from '../customer/useCustomerTypesDict'
import useCountryState from '../useCountry'
import { useTypeIdNumber } from '../useTypeIdNumber'
import { ImageInputFile } from '../../../forms/ImageInput/types'
import { CustomSellSelectCampaign, CustomSellSelectProject } from '../../../specificComponents/sells/components'
interface CreateQuoteCustomerProps {
    customers: CustomerPayload
}

export type CustomerQuoteInfoTy = {
    CustomerId?: number
    projects: TwinDictionary | null
    campaigns: TwinDictionary | null
}

const useCreateQuoteLogic = ({ customers }: CreateQuoteCustomerProps) => {
    const [specialData, setSpecialData] = useState<CustomerQuoteInfoTy>({ CustomerId: undefined, projects: null, campaigns: null })
    const [file, setFile] = useState<ImageInputFile[]>([])
    const { t } = useTwinTranslation()
    const navigate = useNavigate()
    const { country, setCountry } = useCountryState({ defaultCountry: '' })
    const { typesIdNumber } = useTypeIdNumber()
    const { dictCustomerType } = useCustomerTypesDict()
    const memoDefaultCustomerType = useMemo(() => getConfigParam('quoteOrderDefaultCustomerType'), [])
    const addressFields: FormRender<any> = []
    const myCustomerInfo: FormElement<any>[] = []
    const itemsColumns = {
        'reference': { value: 'reference', name: t('reference', 'Referencia') },
        'code_bar': { value: 'code_bar', name: t('barCode', 'Código de barras') },
        'supplier_reference': { value: 'supplier_reference', name: t('supplierRef', 'Referencia proveedor') }
    }

    const createQuote: OnSubmit = useCallback(async (res) => {
        const create = await res.json()
        return navigate(locations.quoteOrderSingle + '/' +  create.id)
    }, [navigate])

    const handleCustomerInfo = useCallback(async (value: string) => {
        setSpecialData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, CustomerId: parseInt(value) }
        })
    }, [setSpecialData])

    const handleCustomerCampaignProject = useCallback(async (_id: string, fieldName: string, value: string) => {
        setSpecialData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, [fieldName]: parseInt(value) }
        })
    }, [setSpecialData])

    const getCustomerCampaigns = useCallback(async () => {
        return await twinFetchPostJSON('/api/app/customer/campaign/customer/getAllCampaignCustomers', {
            where:
            {
                CustomerId: specialData.CustomerId
            },
        })
    }, [specialData.CustomerId])

    const getCustomerProjects = useCallback(async () => {
        return await twinFetchPostJSON('/api/app/customer/customerProject/getAllCustomerProjects', {
            where:
            {
                'CustomerProject.active': true,
                CustomerId: specialData.CustomerId
            },
        })
    }, [specialData.CustomerId])

    const myCustomerProjectsOnSubmit = useCallback(async () => {
        const result = await getCustomerProjects()
        if (result) {
            setSpecialData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                copy.projects = dictionaryComplexFromJsonArr(result)
                return { ...copy }
            })
        }
    }, [getCustomerProjects])

    const myCustomerCampaignOnSubmit = useCallback(async () => {
        const result = await getCustomerCampaigns()
        if (result) {
            setSpecialData((old) => {
                const campaigns: TwinDictionary[] = []
                for (const element of result) {
                    campaigns.push({ ...element?.Campaign, id: element?.id, value: element.id, text: element?.Campaign?.name })
                }
                const copy = JSON.parse(JSON.stringify(old))
                copy.campaigns = campaigns
                return { ...copy }
            })
        }
    }, [getCustomerCampaigns])

    const searchItems = useCallback(async () => {
        const searchedItems = await getCustomerProjects()
        const searchedCampaignsItems = await getCustomerCampaigns()
        if (searchedItems && searchedCampaignsItems) {
            setSpecialData((old) => {
                const campaigns: TwinDictionary[] = []
                for (const element of searchedCampaignsItems) {
                    campaigns.push({ ...element?.Campaign, id: element?.id, value: element.id, text: element?.Campaign?.name })
                }

                const copy = JSON.parse(JSON.stringify(old))
                copy.projects = dictionaryComplexFromJsonArr(searchedItems)
                copy.campaigns = campaigns
                return { ...copy }
            })
        } else {
            setSpecialData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                return { ...copy, projects: null, campaigns: null }
            })
        }
    }, [setSpecialData, getCustomerProjects, getCustomerCampaigns])

    useEffect(() => {
        if (specialData.CustomerId) {
            searchItems()
        }
    }, [searchItems, specialData.CustomerId])

    const customerInfo: FormElement<any>[] = []
    customerInfo.push({
        name: 'CustomerId',
        component: 'Custom',
        render: ({ extraProps }) => {
            return <CustomSelectAndCreateSectionCustomer name='CustomerId' customers={extraProps.customers} label={t('customer', 'Cliente')} onChange={handleCustomerInfo} required={true} />
        },
        extraProps: { customers }
    }) 
    if (specialData.CustomerId !== undefined && specialData.CustomerId  !== 0) {
        customerInfo.push({
            name: 'CustomerProjectId',
            component: 'Custom',
            render: ({ extraProps }) => {
                return <CustomSellSelectProject getCompleteData={myCustomerProjectsOnSubmit} headerData={specialData} items={extraProps.projects} handleChange={handleCustomerCampaignProject} name='CustomerProjectId' orderByOpp={true} viewLabel={false} />
            },
            extraProps: { projects: specialData?.projects || {} }
        })

        customerInfo.push({
            name: 'CampaignCustomerId',
            component: 'Custom',
            render: ({ extraProps }) => {
                return <CustomSellSelectCampaign getCompleteData={myCustomerCampaignOnSubmit} headerData={specialData} label={t('campaign', 'Campaña')} items={extraProps.campaigns} handleChange={handleCustomerCampaignProject} name='CampaignCustomerId' viewLabel={false} />
            },
            extraProps: { campaigns: specialData.campaigns ? specialData.campaigns : [] }
        })
    }

    const fileInfo: FormElement<any>[] = [

        {
            name: 'orderFilePath',
            component: 'FilesInput',
            label: t('uploadExcelOrCSV', 'Subir excel o CSV'),
            acceptFileConditions: {
                'text': ['.csv', '.xlsx']
            },
            onChange: setFile,
        },
    ]
    if (file.length) {
        fileInfo.push(
            {
                name: 'delimiter',
                label: t('delimiter', 'Delimitado'),
                component: 'InputWithLabelMargin',
                defaultValue: ';',
                required: true
            },
            {
                name: 'rowsToSkip',
                label: t('rowsToSkip', 'Filas que saltar'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: true
            },
            {
                name: 'searchByFile',
                label: t('searchBy', 'Buscar por'),
                component: 'CustomSelectWithSearchBar',
                items: itemsColumns,
                required: true
            },
            {
                name: 'posColumnSelected',
                label: t('posColumnSelected', 'Posición columna seleccionada'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: true
            },
            {
                name: 'posQuantityColumn',
                label: t('posQuantityColumn', 'Posición columna cantidad'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: true
            },
            {
                name: 'posNotesColumn',
                label: t('posNotesColumn', 'Posición columna notas'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
        )
    }
    const createCustomer = specialData.CustomerId === 0 
    if (createCustomer) {
        myCustomerInfo.push(
            {
                name: 'name',
                label: t('nameCustomer', 'Nombre del Cliente'),
                component: 'InputWithLabelMargin',
                required: true
            },
            {
                name: 'customer_type',
                label: t('customerType', 'Tipo de cliente'),
                items: dictCustomerType,
                component: 'CustomSelect',
                required: true,
                value: memoDefaultCustomerType
            },
            {
                name: 'type_id_number',
                label: t('typeIdNumber', 'Tipo de documento identificación'),
                component: 'CustomSelect',
                items: typesIdNumber,
                value: 0,
            },
            {
                name: 'id_number',
                label: t('id_number', 'DNI'),
                component: 'InputWithLabelMargin',
                type: 'text',
                required: true,
            }
        )
        addressFields.push(
            {
                cols: 1,
                elements: [
                    {
                        name: 'createAddress',
                        component: 'InputHidden',
                        value: 'true'
                    },
                    {
                        name: 'nameAddress',
                        label: t('nameAddress', 'Nombre de la dirección'),
                        component: 'InputWithLabelMargin',
                        type: 'text',
                        required: true
                    },
                    {
                        name: 'name_to',
                        label: t('recipientName', 'Nombre del receptor'),
                        component: 'InputWithLabelMargin',
                        type: 'text',
                        required: true
                    },
                    {
                        name: 'surname_to',
                        label: t('recipientSurname', 'Apellido del receptor'),
                        component: 'InputWithLabelMargin',
                        type: 'text'
                    },
                ],
            },
            {
                cols: 1,
                title: t('address', 'Dirección'),
                elements: [
                    {
                        name: 'address',
                        label: t('address', 'Dirección'),
                        component: 'InputWithLabelMargin',
                        type: 'text',
                        required: true
                    },
                    {
                        name: 'additional_address',
                        label: t('additional_address', 'Dirección adicional'),
                        component: 'InputWithLabelMargin',
                        type: 'text'
                    },
                    {
                        name: 'postal_code',
                        label: t('postalCode', 'Código Postal'),
                        component: 'InputWithLabelMargin',
                        type: 'text',
                        required: true
                    },
                    {
                        name: 'city',
                        label: t('city', 'Ciudad'),
                        component: 'InputWithLabelMargin',
                        type: 'text',
                        required: true
                    },
                    {
                        name: 'country',
                        label: t('country', 'País'),
                        component: 'CustomSelectWithSearchBar',
                        items: getDictionaryCountries(),
                        sortFunction: sortCountries,
                        required: true,
                        value: '',
                        onChange: (value: string) => setCountry(value)
                    },
                    {
                        name: 'state',
                        label: t('state', 'Provincia'),
                        component: 'CustomSelectWithSearchBar',
                        sortItems: true,
                        items: getDictionaryStates(country),
                        required: true,
                        value: ''
                    },
                    {
                        name: 'createWorkingCenter',
                        component: 'CheckboxMargin',
                        label: t('createWorkingcenter', 'Crear centro de trabajo'),
                    }
                ]
            }
        )
    }

    return { handleCustomerInfo, customerInfo, createQuote, addressFields, myCustomerInfo, fileInfo }
}


export default useCreateQuoteLogic